import { useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { buildDocDetailSql, mapDocDetailRows, QA_DB, QA_COMPANY_ID } from "./sql";
import type { DocListRow } from "./sql";
import {
  computeScorecard,
  type Scorecard, type ClassMetric, type MetricStatus, type Finding, type FindingStatus,
} from "./qaScorecard";
import { getReportForDoc, getReportForTemplate, findDocCase, type TemplateQaReport, type QaVerdict } from "./qaReports";

const STATUS_COLOR: Record<MetricStatus, string> = {
  good: "var(--color-jade, #1E6D5B)",
  warn: "#D97706",
  bad: "var(--color-coral-hover, #DC6A4C)",
};

const FINDING_COLOR: Record<FindingStatus, string> = {
  verbatim: "var(--color-jade, #1E6D5B)",
  covered: "var(--color-jade, #1E6D5B)",
  skipped: "var(--fg-3)",
  partial: "#D97706",
  paraphrased: "var(--color-coral-hover, #DC6A4C)",
  missing: "var(--color-coral-hover, #DC6A4C)",
};

const FINDING_LABEL: Record<FindingStatus, string> = {
  verbatim: "Verbatim",
  covered: "Covered",
  skipped: "Skipped",
  partial: "Partial",
  paraphrased: "Paraphrased",
  missing: "Missing",
};

function fmtK(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function truncate(s: string, n = 220): string {
  if (s.length <= n) return s;
  return s.slice(0, n) + "…";
}

export function DocRow({
  row,
  siblings,
  expanded,
  onToggleExpand,
  onOpenCompare,
  colSpan,
}: {
  row: DocListRow;
  siblings: DocListRow[];
  expanded: boolean;
  onToggleExpand: (id: string) => void;
  onOpenCompare: (id: string) => void;
  colSpan: number;
}) {
  const [scoreRequested, setScoreRequested] = useState(false);
  const [scrollToReportTick, setScrollToReportTick] = useState(0);
  const reportPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollToReportTick === 0) return;
    const el = reportPanelRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [scrollToReportTick]);

  const docDetailSql = useMemo(() => buildDocDetailSql(row.doc_id), [row.doc_id]);
  const detail = useAdminQuery(docDetailSql, [], { ...QA_DB, enabled: scoreRequested });

  const scorecard = useMemo<Scorecard | null>(() => {
    if (!scoreRequested || !detail.data) return null;
    const rows = mapDocDetailRows(detail.data.columns, detail.data.rows as unknown[][]);
    const d = rows[0];
    if (!d?.template_content || !d?.doc_content) return null;
    return computeScorecard(d.template_content, d.doc_content);
  }, [scoreRequested, detail.data]);

  const scoreLoading = scoreRequested && (detail.isLoading || (detail.isFetching && !scorecard));

  const localReport = useMemo<TemplateQaReport | null>(() => {
    return (
      getReportForDoc(row.doc_id) ??
      getReportForTemplate({
        templateExternalId: row.template_external_id,
        templateName: row.template_name,
      })
    );
  }, [row.doc_id, row.template_external_id, row.template_name]);

  const docCase = useMemo(() => {
    if (!localReport) return null;
    return findDocCase(localReport, row.doc_id);
  }, [localReport, row.doc_id]);

  const reportHtml = useMemo(() => {
    if (!localReport) return null;
    return marked.parse(localReport.reportMd, { async: false }) as string;
  }, [localReport]);

  return (
    <>
      <tr
        onClick={() => onOpenCompare(row.doc_id)}
        style={{ cursor: "pointer" }}
      >
        <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.name}
        </td>
        <td style={{ fontSize: 12, color: "var(--fg-2)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.template_name ?? "—"}
        </td>
        <td><StatusBadge status={row.status} /></td>
        <td
          style={{ padding: "4px 8px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              onClick={() => {
                if (!scoreRequested) {
                  setScoreRequested(true);
                  if (!expanded) onToggleExpand(row.doc_id);
                  return;
                }
                onToggleExpand(row.doc_id);
              }}
              style={{ cursor: "pointer" }}
            >
              <ScoreBadge
                scorecard={scorecard}
                requested={scoreRequested}
                loading={scoreLoading}
                expanded={expanded}
              />
            </span>
            {localReport && (
              <span
                onClick={() => {
                  if (!expanded) onToggleExpand(row.doc_id);
                  setScrollToReportTick((n) => n + 1);
                }}
                style={{ cursor: "pointer" }}
                title={`Click to open the /template-qa v${localReport.version} report (${new Date(localReport.timestamp).toLocaleDateString("en-AU")})`}
              >
                <VerdictBadge verdict={localReport.meta.verdict} version={localReport.version} />
              </span>
            )}
            <CopyQaCommandButton
              templateName={row.template_name}
              templateExternalId={row.template_external_id}
              siblings={siblings}
            />
          </div>
        </td>
        <td style={{ color: "var(--fg-3)", fontSize: 12 }}>{fmtDate(row.created_date)}</td>
        <td className="num mono" style={{ fontSize: 11 }}>{fmtK(row.content_len)} ch</td>
        <IdCell id={row.template_external_id} />
        <IdCell id={row.doc_id} />
      </tr>
      {expanded && (
        <tr>
          <td colSpan={colSpan} style={{ padding: 0, background: "var(--bg-2)" }}>
            {scorecard && <ScorecardBreakdown scorecard={scorecard} />}
            {localReport && reportHtml && (
              <LocalReportPanel
                report={localReport}
                docCase={docCase}
                html={reportHtml}
                panelRef={reportPanelRef}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function VerdictBadge({ verdict, version }: { verdict: QaVerdict; version: number }) {
  const isFail = verdict === "FAIL";
  const color = isFail ? "var(--color-coral-hover, #DC6A4C)" : "var(--color-jade, #1E6D5B)";
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        fontSize: 10, fontWeight: 700,
        padding: "2px 8px",
        border: `1px solid ${color}`,
        borderRadius: 3,
        color,
        background: `${color}10`,
      }}
    >
      {verdict}
      <span style={{ color: "var(--fg-3)", fontWeight: 400 }}>v{version}</span>
    </span>
  );
}

function LocalReportPanel({
  report,
  docCase,
  html,
  panelRef,
}: {
  report: TemplateQaReport;
  docCase: { client: string; name?: string } | null;
  html: string;
  panelRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={panelRef}
      style={{
        padding: "16px 24px",
        borderTop: "1px solid var(--border-default)",
        background: "var(--bg-1)",
      }}
    >
      <div style={{
        display: "flex", alignItems: "baseline", gap: 12, marginBottom: 10, flexWrap: "wrap",
        paddingBottom: 6, borderBottom: "1px solid var(--border-default)",
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--fg-3)" }}>
          /template-qa Report
        </span>
        <VerdictBadge verdict={report.meta.verdict} version={report.version} />
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
          {report.templateName} · {report.meta.document_type} · run {new Date(report.timestamp).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
        {docCase && (
          <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
            This doc covered as case: <strong>{docCase.client}</strong>
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-3)" }}>
          {report.meta.critical_alterations} CRITICAL · {report.meta.hallucinations} hallucinations · {report.meta.unverified_files} unverified
        </span>
      </div>
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        style={{ maxWidth: 1100, fontSize: 13, lineHeight: 1.6, color: "var(--fg-1)" }}
      />
    </div>
  );
}

function ScoreBadge({
  scorecard,
  requested,
  loading,
  expanded,
}: {
  scorecard: Scorecard | null;
  requested: boolean;
  loading: boolean;
  expanded: boolean;
}) {
  if (!requested) {
    return (
      <span
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          fontSize: 10, fontWeight: 600,
          padding: "2px 8px",
          border: "1px dashed var(--border-default)",
          borderRadius: 3,
          color: "var(--fg-3)",
        }}
      >
        Compute score
      </span>
    );
  }
  if (loading) {
    return <span style={{ fontSize: 11, color: "var(--fg-3)" }}>computing…</span>;
  }
  if (!scorecard) {
    return <span style={{ fontSize: 11, color: "var(--fg-3)" }}>—</span>;
  }
  const color = STATUS_COLOR[scorecard.status];
  const score = Math.round(scorecard.overall);
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 12, fontWeight: 600, color,
      }}
    >
      <span
        style={{
          display: "inline-block", width: 8, height: 8, borderRadius: 4,
          background: color,
        }}
      />
      {score}
      <span style={{ color: "var(--fg-3)", fontSize: 10, fontWeight: 400, marginLeft: 2 }}>
        {expanded ? "▾" : "▸"}
      </span>
    </span>
  );
}

function ScorecardBreakdown({ scorecard }: { scorecard: Scorecard }) {
  const confidence = Math.round(scorecard.classifierConfidence);
  const confidenceColor = confidence >= 70 ? STATUS_COLOR.good : confidence >= 40 ? STATUS_COLOR.warn : STATUS_COLOR.bad;
  return (
    <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{
        display: "flex", alignItems: "baseline", gap: 12, paddingBottom: 8,
        borderBottom: "1px solid var(--border-default)", flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--fg-3)" }}>
          QA Scorecard
        </span>
        <span style={{ fontSize: 18, fontWeight: 600, color: STATUS_COLOR[scorecard.status] }}>
          {Math.round(scorecard.overall)}
          <span style={{ fontSize: 11, fontWeight: 400, color: "var(--fg-3)", marginLeft: 4 }}>/100</span>
        </span>
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
          Equal-weight average across the 4 class metrics below.
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-3)" }}>
          Classifier confidence:{" "}
          <span style={{ color: confidenceColor, fontWeight: 600 }}>{confidence}%</span>{" "}
          of output blocks matched a template region. Low confidence = scorecard below is unreliable.
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {scorecard.metrics.map((m) => (
          <MetricBlock key={m.key} metric={m} />
        ))}
      </div>
    </div>
  );
}

function MetricBlock({ metric }: { metric: ClassMetric }) {
  const [open, setOpen] = useState(false);
  const color = metric.total === 0 ? "var(--fg-3)" : STATUS_COLOR[metric.status];
  return (
    <div style={{ borderTop: "1px solid var(--border-default)" }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 4px", cursor: "pointer", fontSize: 12,
        }}
      >
        <span style={{ width: 16, color: "var(--fg-3)" }}>{open ? "▾" : "▸"}</span>
        <span style={{ width: 240, color: "var(--fg-1)" }}>{metric.name}</span>
        <span style={{ flex: 1, color: "var(--fg-2)", fontSize: 11 }}>
          {metric.total === 0 ? "(no regions of this class in template)" : metric.display}
        </span>
        <div style={{ width: 200 }}>
          <ScoreBar score={metric.rawScore} color={color} />
        </div>
        <span style={{ width: 50, fontFamily: "var(--font-mono)", color, fontWeight: 600, textAlign: "right" }}>
          {metric.total === 0 ? "—" : Math.round(metric.rawScore)}
        </span>
      </div>
      {open && (
        <div style={{ padding: "0 8px 12px 30px", fontSize: 11, color: "var(--fg-2)" }}>
          <div style={{ marginBottom: 8 }}>{metric.description}</div>
          {metric.total > 0 && <FindingList findings={metric.findings} />}
        </div>
      )}
    </div>
  );
}

function FindingList({ findings }: { findings: Finding[] }) {
  const groups = new Map<FindingStatus, Finding[]>();
  for (const f of findings) {
    const arr = groups.get(f.status) ?? [];
    arr.push(f);
    groups.set(f.status, arr);
  }
  const order: FindingStatus[] = ["paraphrased", "missing", "partial", "skipped", "verbatim", "covered"];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {order.filter((s) => groups.has(s)).map((s) => (
        <FindingGroup key={s} status={s} findings={groups.get(s)!} />
      ))}
    </div>
  );
}

function FindingGroup({ status, findings }: { status: FindingStatus; findings: Finding[] }) {
  const [open, setOpen] = useState(status === "paraphrased" || status === "missing" || status === "partial");
  const color = FINDING_COLOR[status];
  return (
    <div>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}
      >
        <span style={{ color: "var(--fg-3)", width: 12 }}>{open ? "▾" : "▸"}</span>
        <span
          style={{
            display: "inline-block", padding: "1px 6px", borderRadius: 3,
            background: `${color}1f`, color, fontSize: 10, fontWeight: 600,
          }}
        >
          {FINDING_LABEL[status]}
        </span>
        <span style={{ color: "var(--fg-3)" }}>{findings.length}</span>
      </div>
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingLeft: 18, paddingTop: 4 }}>
          {findings.map((f, i) => (
            <FindingRow key={i} finding={f} />
          ))}
        </div>
      )}
    </div>
  );
}

function FindingRow({ finding }: { finding: Finding }) {
  if (finding.parentHeading) {
    return (
      <div style={{ padding: "4px 8px", background: "var(--bg-1)", borderRadius: 3, fontSize: 11 }}>
        <div style={{ color: "var(--fg-1)", fontWeight: 500 }}>{finding.parentHeading}</div>
        {finding.wordCount !== undefined && (
          <div style={{ color: "var(--fg-3)", fontSize: 10, marginTop: 2 }}>
            {finding.wordCount} words under heading
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ padding: "4px 8px", background: "var(--bg-1)", borderRadius: 3, fontSize: 11 }}>
      <div style={{ color: "var(--fg-2)", marginBottom: finding.outputText ? 4 : 0 }}>
        <span style={{ color: "var(--fg-3)", fontSize: 10, marginRight: 4 }}>Template:</span>
        {truncate(finding.templateText)}
      </div>
      {finding.outputText && (
        <div style={{ color: "var(--fg-2)" }}>
          <span style={{ color: "var(--fg-3)", fontSize: 10, marginRight: 4 }}>Output:</span>
          {truncate(finding.outputText)}
        </div>
      )}
      {finding.score !== undefined && (
        <div style={{ color: "var(--fg-3)", fontSize: 10, marginTop: 2 }}>
          score: {finding.score.toFixed(2)}
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score, color }: { score: number; color: string }) {
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div style={{ width: "100%", height: 6, background: "var(--bg-3, #e8e8e8)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ width: `${clamped}%`, height: "100%", background: color }} />
    </div>
  );
}

function IdCell({ id }: { id: string | null }) {
  return (
    <td style={{ fontSize: 10, color: "var(--fg-3)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
      {id ?? "—"}
    </td>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === "complete" || status === "completed"
      ? "var(--color-jade, #1E6D5B)"
      : status === "failed"
      ? "var(--color-coral-hover, #DC6A4C)"
      : "var(--fg-3)";
  return (
    <span style={{ fontSize: 11, color, fontWeight: 500, textTransform: "capitalize" }}>
      {status}
    </span>
  );
}

const CASE_NAMES = ["mills", "snow", "nguyen", "chen", "thompson", "vanzyl"] as const;
type CaseName = typeof CASE_NAMES[number];

function inferDocumentType(templateName: string | null): "SOA" | "ROA" | null {
  if (!templateName) return null;
  const t = templateName.toLowerCase();
  if (/\broa\b/.test(t)) return "ROA";
  if (/\bsoa\b/.test(t)) return "SOA";
  return null;
}

function caseForDocName(docName: string | null): CaseName | null {
  if (!docName) return null;
  const lowered = docName.toLowerCase();
  for (const c of CASE_NAMES) if (lowered.includes(c)) return c;
  return null;
}

function buildQaCommand(
  templateName: string | null,
  templateExternalId: string | null,
  siblings: DocListRow[],
): string {
  const docType = inferDocumentType(templateName);
  const caseMatrix: CaseName[] =
    docType === "ROA"
      ? ["mills", "snow", "nguyen", "chen", "thompson", "vanzyl"]
      : ["mills", "snow", "nguyen", "chen", "thompson"];

  // Map each sibling doc to a client case, keep the most recent per case.
  const byCase = new Map<CaseName, DocListRow>();
  for (const s of siblings) {
    const c = caseForDocName(s.name);
    if (!c) continue;
    if (!caseMatrix.includes(c)) continue;
    const existing = byCase.get(c);
    if (!existing || existing.created_date < s.created_date) byCase.set(c, s);
  }

  const lines: string[] = [];
  lines.push("/template-qa");
  lines.push("");
  lines.push(`Run the full /template-qa workflow once for this template. Spawn one sub-agent per case in parallel (Phase 2), run the parent hallucination verifier (Phase 3), aggregate, and produce a single report covering all ${caseMatrix.length} cases.`);
  lines.push("");
  lines.push(`Template: ${templateName ?? "(see id below)"}`);
  if (templateExternalId) lines.push(`Template external_id: ${templateExternalId}`);
  if (docType) lines.push(`Document type: ${docType}`);
  lines.push(`Company external_id: ${QA_COMPANY_ID}`);
  lines.push("");
  lines.push("Fetch the licensee original template via the staging Supabase MCP using the template_external_id (file.file_type = 'template').");
  lines.push("");
  lines.push(`Generated outputs to compare against (one per demo client case, all ${caseMatrix.length} should run in this single invocation):`);
  for (const c of caseMatrix) {
    const doc = byCase.get(c);
    if (doc) {
      lines.push(`- ${c}: document.external_id = ${doc.doc_id}  (name: ${doc.name})`);
    } else {
      lines.push(`- ${c}: (no matching document visible in dashboard right now; if needed, query Supabase for documents under this template with '${c}' in the name)`);
    }
  }
  lines.push("");
  lines.push(`Each generated output's HTML lives in staging Supabase as document.content (keyed by external_id). Pull each one inline before spawning sub-agents.`);
  lines.push("");
  lines.push("Write outputs to outputs/template-qa/ as the skill specifies (reports/, meta.json, score-log.csv).");
  return lines.join("\n");
}

function CopyQaCommandButton({
  templateName,
  templateExternalId,
  siblings,
}: {
  templateName: string | null;
  templateExternalId: string | null;
  siblings: DocListRow[];
}) {
  const [copied, setCopied] = useState(false);
  const command = useMemo(
    () => buildQaCommand(templateName, templateExternalId, siblings),
    [templateName, templateExternalId, siblings],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={`Click to copy this prompt:\n\n${command}`}
      style={{
        fontSize: 10, fontWeight: 600,
        padding: "2px 8px",
        border: "1px solid var(--border-default)",
        borderRadius: 3,
        background: copied ? "rgba(30,109,91,0.12)" : "transparent",
        color: copied ? "var(--color-jade, #1E6D5B)" : "var(--fg-2)",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {copied ? "Copied ✓" : "Copy /template-qa"}
    </button>
  );
}
